// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceFilter } from '@/billing/api/get-invoices';
import {
  InvoiceListItem,
  InvoiceSortProperty,
  invoiceAgingBucketSchema,
  invoiceSortPropertySchema,
} from '@/billing/api/types';
import { AddInvoiceDialog } from '@/billing/components/add-invoice-dialog';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { GenerateInvoicesDialog } from '@/billing/components/generate-invoices-dialog';
import {
  AGING_BUCKET_VALUES,
  AGING_FILTER_LABELS,
  INVOICE_DATE_SHORTCUTS,
  INVOICE_STATUS_FILTER_OPTIONS,
  PAGE_SIZE,
} from '@/billing/components/invoice-status-constants';
import { InvoiceSummaryCards } from '@/billing/components/invoice-summary-cards';
import { InvoiceTableRow } from '@/billing/components/invoice-table-row';
import { useCharges } from '@/billing/hooks/use-charges';
import { useInvoices } from '@/billing/hooks/use-invoices';
import { ActionMenuItem } from '@/components/action-menu-item';
import { EducationalTooltip } from '@/components/educational-tooltip';
import { EmptyState } from '@/components/empty-state';
import {
  AmountRange,
  FilterAmountRangePopover,
} from '@/components/filter-amount-range-popover';
import { FilterDateRangePopover } from '@/components/filter-date-range-popover';
import { FilterMenu } from '@/components/filter-menu';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { Link } from '@/components/link';
import { PageContent } from '@/components/page-content';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { TableControlContainer } from '@/components/table-control-container';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  AddRounded,
  ArrowDropDownRounded,
  ArrowForwardRounded,
  ClearRounded,
  InfoOutlined,
  SearchRounded,
} from '@/constants/hardfin-standard-icons';
import {
  INVOICE_STATUS_LABELS,
  InvoiceStatus,
  InvoiceStatusFilter,
  invoiceStatusFilterSchema,
} from '@/constants/invoice-status';
import { SEARCH_TERM_MAXIMUM_CHARACTERS } from '@/constants/search-term';
import { Sort, invert, sortDirectionSchema } from '@/constants/sort';
import { getCustomers } from '@/customer/api/get-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTableSort } from '@/hooks/use-table-sort';
import { getSparseProjects } from '@/project/api/get-sparse-projects';
import { searchTermSchema } from '@/utilities/search-term';
import {
  FIFTEEN_MINUTES_MS,
  FIVE_MINUTES_MS,
} from '@/utilities/time-constants';
import {
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Menu,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  status: invoiceStatusFilterSchema.optional().catch(undefined),
  customerKeys: z.array(z.string()).optional(),
  projectKeys: z.array(z.string()).optional(),
  agingBuckets: z.array(invoiceAgingBucketSchema).optional().catch(undefined),
  query: searchTermSchema,
  page: z.number().optional(),
  invoiceDateStart: z.string().optional().catch(undefined),
  invoiceDateEnd: z.string().optional().catch(undefined),
  dueDateStart: z.string().optional().catch(undefined),
  dueDateEnd: z.string().optional().catch(undefined),
  amountMin: z.number().optional().catch(undefined),
  amountMax: z.number().optional().catch(undefined),
  sortBy: invoiceSortPropertySchema.optional().catch(undefined),
  sortDirection: sortDirectionSchema.optional(),
});

export const Route = createFileRoute('/_app/billing/invoices/')({
  component: InvoicesRoute,
  validateSearch: searchSchema,
});

// ---------------------------------------------------------------------------
// Amount filter bounds derivation — mirrors the charges listing. The backend
// returns the min/max across ALL invoices (ignoring listing filters) so the
// slider extents stay stable as the user narrows the view.
// ---------------------------------------------------------------------------

// Fallback bounds used when the listing response has not arrived yet or the
// org has zero invoices. The filter renders disabled in this state so the
// numeric values are only used to keep the MUI Slider from crashing on an
// empty range.
const AMOUNT_FILTER_FALLBACK_MIN = 0;
const AMOUNT_FILTER_FALLBACK_MAX = 1;
// Cent granularity — the smallest step the data-driven slider can produce
// regardless of how tight the [min, max] range is.
const AMOUNT_FILTER_MIN_STEP = 0.01;
// Divisor for deriving the slider step from the [min, max] spread. A value
// of 20 gives users 20 tick positions across the range, which matches the
// charges listing and keeps the granularity coarse enough to be draggable.
const AMOUNT_FILTER_STEP_DIVISOR = 20;
// Used to widen a zero-width range (single-invoice org, or every invoice at
// the same amount) just enough for the slider to render usefully.
const AMOUNT_FILTER_SINGLE_POINT_PAD = 1;

// Number of columns in the invoice table — drives the cold-load skeleton
// so the placeholder row widths match the real table layout. Keep in sync
// with the <TableHead> below.
const INVOICE_COLUMN_COUNT = 8;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function InvoicesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const globalNavigate = useNavigate();
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const addInvoiceDisclosure = useDisclosure();
  const generateInvoicesDisclosure = useDisclosure();
  const actionMenuDisclosure = useDisclosure();
  const actionGroupRef = useRef<HTMLDivElement>(null);

  // BILLABLE charge count drives the "Generate invoices" default button:
  // the sweep endpoint operates on every billable charge org-wide, so we
  // fetch the unfiltered status summary the same way billing.charges.tsx
  // does. pageSize: 1 keeps the listing payload minimal — only the summary
  // is consumed here.
  const { statusSummary: chargeStatusSummary } = useCharges({
    filter: { pageSize: 1 },
  });
  const billableCount = chargeStatusSummary
    ? chargeStatusSummary.byCurrency.reduce(
        (sum, bucket) => sum + bucket.billableCount,
        0
      )
    : 0;
  const isChargeSummaryLoading = chargeStatusSummary === null;
  const generateDisabled = isChargeSummaryLoading || billableCount === 0;

  // Sort state derived from URL — default to most-recent invoice date first.
  // Memoized so useCallback dependencies are stable across renders.
  const sort: Sort<InvoiceSortProperty> = useMemo(
    () => ({
      property: search.sortBy ?? 'invoiceDate',
      direction: search.sortDirection ?? 'desc',
    }),
    [search.sortBy, search.sortDirection]
  );
  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  const createSortHandler = useCallback(
    (property: InvoiceSortProperty) => () => {
      const newDirection =
        sort.property === property ? invert(sort.direction) : 'asc';
      void navigate({
        search: {
          ...search,
          sortBy: property,
          sortDirection: newDirection,
          page: undefined,
        },
      });
    },
    [sort, search, navigate]
  );

  const activeStatus: InvoiceStatusFilter = search.status ?? 'all';
  const activeCustomerKeys = search.customerKeys ?? [];
  const activeProjectKeys = search.projectKeys ?? [];
  const activeAgingBuckets = search.agingBuckets ?? [];
  const page = search.page ?? 1;
  const invoiceDateRange: DateRange<DateTime> = [
    search.invoiceDateStart ? DateTime.fromISO(search.invoiceDateStart) : null,
    search.invoiceDateEnd ? DateTime.fromISO(search.invoiceDateEnd) : null,
  ];
  const dueDateRange: DateRange<DateTime> = [
    search.dueDateStart ? DateTime.fromISO(search.dueDateStart) : null,
    search.dueDateEnd ? DateTime.fromISO(search.dueDateEnd) : null,
  ];
  const amountRange: AmountRange = [
    search.amountMin ?? null,
    search.amountMax ?? null,
  ];

  const isInvoiceDateFilterActive =
    invoiceDateRange[0] !== null || invoiceDateRange[1] !== null;
  const isDueDateFilterActive =
    dueDateRange[0] !== null || dueDateRange[1] !== null;
  const isAmountFilterActive =
    search.amountMin !== undefined || search.amountMax !== undefined;

  // -------------------------------------------------------------------------
  // Fetch invoice data
  // -------------------------------------------------------------------------

  const invoiceFilter: InvoiceFilter = {
    pageSize: PAGE_SIZE,
    pageNumber: page,
    sort,
    search: search.query,
    status: activeStatus === 'all' ? undefined : activeStatus,
    customerKeys:
      activeCustomerKeys.length > 0 ? activeCustomerKeys : undefined,
    projectKeys: activeProjectKeys.length > 0 ? activeProjectKeys : undefined,
    agingBuckets:
      activeAgingBuckets.length > 0 ? activeAgingBuckets : undefined,
    invoiceDateStart: search.invoiceDateStart,
    invoiceDateEnd: search.invoiceDateEnd,
    dueDateStart: search.dueDateStart,
    dueDateEnd: search.dueDateEnd,
    amountMin: search.amountMin,
    amountMax: search.amountMax,
  };

  const {
    invoices,
    totalCount,
    summary,
    isPlaceholderData,
    amountRange: listingAmountRange,
  } = useInvoices({
    filter: invoiceFilter,
  });
  const downloadURL = new URL(
    '/v1/export/invoices',
    import.meta.env.VITE_API_URL
  );

  // Data-driven bounds for the amount filter slider. Same derivation as the
  // charges listing — see billing.charges.tsx:amountFilterBounds for the
  // rationale around fallback, single-point widening, and step rounding.
  const amountFilterBounds = useMemo(() => {
    const fallback = {
      min: AMOUNT_FILTER_FALLBACK_MIN,
      max: AMOUNT_FILTER_FALLBACK_MAX,
      step: AMOUNT_FILTER_MIN_STEP,
      isReady: false,
    };
    if (!listingAmountRange) {
      return fallback;
    }
    const rawMin = Number(listingAmountRange.min);
    const rawMax = Number(listingAmountRange.max);
    if (Number.isNaN(rawMin) || Number.isNaN(rawMax) || rawMax < rawMin) {
      return fallback;
    }
    if (rawMax === rawMin) {
      return {
        min: rawMin,
        max: rawMax + AMOUNT_FILTER_SINGLE_POINT_PAD,
        step: AMOUNT_FILTER_MIN_STEP,
        isReady: true,
      };
    }
    const step = Math.max(
      AMOUNT_FILTER_MIN_STEP,
      Math.round(((rawMax - rawMin) / AMOUNT_FILTER_STEP_DIVISOR) * 100) / 100
    );
    return { min: rawMin, max: rawMax, step, isReady: true };
  }, [listingAmountRange]);

  // Page-overshoot recovery should live in the shared table footer instead of
  // route-specific guards. See ai/todo/pagination-footer-overshoot-recovery.md.

  // -------------------------------------------------------------------------
  // Fetch filter option lists
  // -------------------------------------------------------------------------

  const { data: customersData } = useQuery({
    queryKey: ['customers', 'filter-options'],
    queryFn: () => getCustomers(),
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIFTEEN_MINUTES_MS,
  });
  const customerOptions = (customersData?.customers ?? []).map((c) => ({
    id: c.key,
    label: c.name,
    isActive: activeCustomerKeys.includes(c.key),
  }));

  const { data: projectsData } = useQuery({
    queryKey: ['sparse-projects', 'filter-options'],
    queryFn: getSparseProjects,
    staleTime: FIVE_MINUTES_MS,
    gcTime: FIFTEEN_MINUTES_MS,
  });
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    id: p.key,
    label: p.name,
    isActive: activeProjectKeys.includes(p.key),
  }));

  const agingOptions = AGING_BUCKET_VALUES.map((bucket) => ({
    id: bucket,
    label: AGING_FILTER_LABELS[bucket],
    isActive: activeAgingBuckets.includes(bucket),
  }));

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleStatusChange = (newStatus: InvoiceStatusFilter) => {
    void navigate({
      search:
        newStatus === 'all'
          ? { ...omit(search, 'status', 'page') }
          : { ...search, status: newStatus, page: undefined },
    });
  };

  const handleChangeCustomer = (customerKeys: string[]) => {
    void navigate({
      search:
        customerKeys.length === 0
          ? { ...omit(search, 'customerKeys', 'page') }
          : { ...search, customerKeys, page: undefined },
    });
  };

  const handleChangeProject = (projectKeys: string[]) => {
    void navigate({
      search:
        projectKeys.length === 0
          ? { ...omit(search, 'projectKeys', 'page') }
          : { ...search, projectKeys, page: undefined },
    });
  };

  const handleChangeAging = (buckets: string[]) => {
    // FilterMenu returns `id: string`; narrow back to the enum at this boundary.
    const narrowed = buckets.filter(
      (b): b is (typeof AGING_BUCKET_VALUES)[number] =>
        (AGING_BUCKET_VALUES as readonly string[]).includes(b)
    );
    void navigate({
      search:
        narrowed.length === 0
          ? { ...omit(search, 'agingBuckets', 'page') }
          : { ...search, agingBuckets: narrowed, page: undefined },
    });
  };

  const [searchInput, setSearchInput] = useState(search.query ?? '');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  const handleSearch = (query: string) => {
    setSearchInput(query);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      const latestSearch = searchRef.current;
      void navigate({
        search:
          query.length === 0
            ? { ...omit(latestSearch, 'query', 'page') }
            : { ...latestSearch, query, page: undefined },
      });
    }, 300);
  };

  const handleChangeInvoiceDateRange = (dateRange: DateRange<DateTime>) => {
    void navigate({
      search: {
        ...omit(search, ['invoiceDateStart', 'invoiceDateEnd', 'page']),
        invoiceDateStart: dateRange[0]?.toISODate() ?? undefined,
        invoiceDateEnd: dateRange[1]?.toISODate() ?? undefined,
      },
    });
  };

  const handleChangeDueDateRange = (dateRange: DateRange<DateTime>) => {
    void navigate({
      search: {
        ...omit(search, ['dueDateStart', 'dueDateEnd', 'page']),
        dueDateStart: dateRange[0]?.toISODate() ?? undefined,
        dueDateEnd: dateRange[1]?.toISODate() ?? undefined,
      },
    });
  };

  const handleChangeAmountRange = (range: AmountRange) => {
    void navigate({
      search: {
        ...omit(search, ['amountMin', 'amountMax', 'page']),
        amountMin: range[0] ?? undefined,
        amountMax: range[1] ?? undefined,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    void navigate({
      search:
        newPage === 1
          ? { ...omit(search, 'page') }
          : { ...search, page: newPage },
    });
  };

  const handleInvoiceClick = (
    invoice: InvoiceListItem,
    event: MouseEvent<HTMLElement>
  ) => {
    // Ctrl/Cmd-click opens in a new tab via the raw URL so middle-click and
    // shareable links behave the way users expect. Normal clicks go through
    // TanStack Router with typed params for type safety.
    if (event.metaKey || event.ctrlKey) {
      window.open(`/billing/invoices/${invoice.key}`, '_blank');
      return;
    }
    void globalNavigate({
      to: '/billing/invoices/$invoiceKey',
      params: { invoiceKey: invoice.key },
    });
  };

  const hasActiveFilters =
    activeCustomerKeys.length > 0 ||
    activeProjectKeys.length > 0 ||
    activeAgingBuckets.length > 0 ||
    isInvoiceDateFilterActive ||
    isDueDateFilterActive ||
    isAmountFilterActive ||
    (search.query ?? '').length > 0;

  const handleClearFilters = () => {
    setSearchInput('');
    void navigate({
      search: omit(search, [
        'customerKeys',
        'projectKeys',
        'agingBuckets',
        'invoiceDateStart',
        'invoiceDateEnd',
        'dueDateStart',
        'dueDateEnd',
        'amountMin',
        'amountMax',
        'query',
        'page',
      ]),
    });
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <PageContent>
      {/* Header */}
      <Stack sx={{ mb: 2 }}>
        <SectionBreadcrumbs
          items={[BILLING_BREADCRUMB_ROOT, { label: 'Invoices' }]}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Invoices</Typography>
          <Button
            component={Link}
            to="/billing/aging"
            size="small"
            endIcon={<ArrowForwardRounded fontSize="small" />}
            sx={{ textTransform: 'none' }}
          >
            View A/R aging
          </Button>
        </Stack>
      </Stack>

      <AddInvoiceDialog
        isOpen={addInvoiceDisclosure.isOpen}
        onClose={addInvoiceDisclosure.close}
      />

      <GenerateInvoicesDialog
        isOpen={generateInvoicesDisclosure.isOpen}
        onClose={generateInvoicesDisclosure.close}
        billableCount={billableCount}
      />

      {/* Summary cards */}
      <InvoiceSummaryCards summary={summary} />

      <Stack spacing={2}>
        {/* Status filter toggle + educational tooltip; Add invoice on the right */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FilterToggleGroup
              value={activeStatus}
              options={INVOICE_STATUS_FILTER_OPTIONS}
              onChange={handleStatusChange}
              ariaLabel="invoice status filter"
            />
            <IconButton
              ref={infoButtonRef}
              size="small"
              onClick={() => setInfoOpen(true)}
              aria-label="Learn about invoice statuses"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Stack>
          <ButtonGroup size="small" variant="contained" ref={actionGroupRef}>
            <Tooltip
              arrow
              title={
                !isChargeSummaryLoading && billableCount === 0
                  ? 'No billable charges available'
                  : ''
              }
            >
              {/* Tooltip needs a non-disabled child to attach a listener,
                  so the disabled button is wrapped in a span. */}
              <span>
                <Button
                  onClick={generateInvoicesDisclosure.open}
                  disabled={generateDisabled}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Generate invoices
                </Button>
              </span>
            </Tooltip>
            <Button
              onClick={actionMenuDisclosure.open}
              aria-label="More invoice creation actions"
            >
              <ArrowDropDownRounded fontSize="small" />
            </Button>
          </ButtonGroup>
          <Menu
            open={actionMenuDisclosure.isOpen}
            onClose={actionMenuDisclosure.close}
            onClick={actionMenuDisclosure.close}
            anchorEl={actionGroupRef.current}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            MenuListProps={{ disablePadding: true }}
            slotProps={{
              paper: {
                sx: { maxWidth: '100vw', width: '25rem', marginTop: 0.5 },
              },
            }}
          >
            <ActionMenuItem
              icon={<AddRounded color="action" fontSize="small" />}
              label="Create manual invoice"
              description="Start an empty draft invoice"
              onClick={addInvoiceDisclosure.open}
            />
          </Menu>
        </Stack>

        <EducationalTooltip
          title="Invoice statuses"
          isOpen={infoOpen}
          onClose={() => setInfoOpen(false)}
          anchorElement={infoButtonRef.current}
          placement="bottom-start"
        >
          <Stack spacing={1}>
            <Typography variant="body2">
              <strong>{INVOICE_STATUS_LABELS.DRAFT}:</strong> Invoice is being
              prepared, editable
            </Typography>
            <Typography variant="body2">
              <strong>{INVOICE_STATUS_LABELS.OPEN}:</strong> Finalized and sent
              to customer
            </Typography>
            <Typography variant="body2">
              <strong>{INVOICE_STATUS_LABELS.PARTIALLY_PAID}:</strong> Partial
              payment recorded, balance outstanding
            </Typography>
            <Typography variant="body2">
              <strong>{INVOICE_STATUS_LABELS.PAID}:</strong> Full payment
              recorded
            </Typography>
            <Typography variant="body2">
              <strong>{INVOICE_STATUS_LABELS.VOID}:</strong> Cancelled
            </Typography>
          </Stack>
        </EducationalTooltip>

        {/* Filter + search row */}
        <TableControlContainer paddingTop={0}>
          <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
            <FilterMenu
              label="Customer"
              options={customerOptions}
              onChangeOption={(id, isActive) => {
                handleChangeCustomer(
                  isActive
                    ? [...activeCustomerKeys, id]
                    : activeCustomerKeys.filter((c) => c !== id)
                );
              }}
            />
            <FilterMenu
              label="Project"
              isSearchable
              options={projectOptions}
              onChangeOption={(id, isActive) => {
                handleChangeProject(
                  isActive
                    ? [...activeProjectKeys, id]
                    : activeProjectKeys.filter((p) => p !== id)
                );
              }}
            />
            <FilterDateRangePopover
              label="Invoice date"
              value={invoiceDateRange}
              onChange={handleChangeInvoiceDateRange}
              shortcuts={INVOICE_DATE_SHORTCUTS}
            />
            <FilterDateRangePopover
              label="Due date"
              value={dueDateRange}
              onChange={handleChangeDueDateRange}
              shortcuts={INVOICE_DATE_SHORTCUTS}
            />
            <FilterMenu
              label="Aging"
              options={agingOptions}
              onChangeOption={(id, isActive) => {
                handleChangeAging(
                  isActive
                    ? [...activeAgingBuckets, id]
                    : activeAgingBuckets.filter((bucket) => bucket !== id)
                );
              }}
            />
            <FilterAmountRangePopover
              label="Amount"
              min={amountFilterBounds.min}
              max={amountFilterBounds.max}
              step={amountFilterBounds.step}
              isDisabled={!amountFilterBounds.isReady}
              value={amountRange}
              onChange={handleChangeAmountRange}
            />
            {hasActiveFilters && (
              <Button size="small" onClick={handleClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
          <TextField
            size="small"
            placeholder="Search invoices"
            value={searchInput}
            inputProps={{ maxLength: SEARCH_TERM_MAXIMUM_CHARACTERS }}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded
                    fontSize="small"
                    sx={{ color: 'neutral.400' }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleSearch('')}
                    edge="end"
                    size="small"
                  >
                    <ClearRounded />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ width: { xs: '100%', sm: '20rem' } }}
          />
        </TableControlContainer>

        {/* Invoices table — sits flush against the filter row above. While
            the first fetch is in flight `invoices` is `null`; we render
            skeleton rows so the empty state doesn't flash as a lie. The
            same skeleton treatment applies when a filter change supplies
            placeholder data from a previous query key (`isPlaceholderData`)
            and there are no rows to show — without it the empty state
            flashes before the new payload arrives. `isPlaceholderData` is
            used instead of `isFetching` so that background refetches of
            an already-resolved empty result do not briefly replace the
            empty state with skeletons. */}
        {invoices !== null && invoices.length === 0 && !isPlaceholderData ? (
          <EmptyState title={statusFilterLabel(activeStatus)} />
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mt: '0 !important' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    width="14%"
                    sortDirection={getSortDirection('invoiceNumber')}
                  >
                    <TableSortLabel
                      active={isLabelActive('invoiceNumber')}
                      direction={getLabelDirection('invoiceNumber')}
                      onClick={createSortHandler('invoiceNumber')}
                    >
                      Invoice #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="16%"
                    sortDirection={getSortDirection('customer')}
                  >
                    <TableSortLabel
                      active={isLabelActive('customer')}
                      direction={getLabelDirection('customer')}
                      onClick={createSortHandler('customer')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="16%"
                    sortDirection={getSortDirection('project')}
                  >
                    <TableSortLabel
                      active={isLabelActive('project')}
                      direction={getLabelDirection('project')}
                      onClick={createSortHandler('project')}
                    >
                      Project
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="12%"
                    sortDirection={getSortDirection('invoiceDate')}
                  >
                    <TableSortLabel
                      active={isLabelActive('invoiceDate')}
                      direction={getLabelDirection('invoiceDate')}
                      onClick={createSortHandler('invoiceDate')}
                    >
                      Invoice date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="14%"
                    sortDirection={getSortDirection('dueDate')}
                  >
                    <TableSortLabel
                      active={isLabelActive('dueDate')}
                      direction={getLabelDirection('dueDate')}
                      onClick={createSortHandler('dueDate')}
                    >
                      Due date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="10%"
                    align="right"
                    sortDirection={getSortDirection('total')}
                  >
                    <TableSortLabel
                      active={isLabelActive('total')}
                      direction={getLabelDirection('total')}
                      onClick={createSortHandler('total')}
                    >
                      Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="10%"
                    align="right"
                    sortDirection={getSortDirection('balance')}
                  >
                    <TableSortLabel
                      active={isLabelActive('balance')}
                      direction={getLabelDirection('balance')}
                      onClick={createSortHandler('balance')}
                    >
                      Balance
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    width="8%"
                    sortDirection={getSortDirection('status')}
                  >
                    <TableSortLabel
                      active={isLabelActive('status')}
                      direction={getLabelDirection('status')}
                      onClick={createSortHandler('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices === null ||
                (invoices.length === 0 && isPlaceholderData)
                  ? Array.from({ length: PAGE_SIZE }).map((_, rowIndex) => (
                      <TableRow key={`skeleton-${rowIndex}`}>
                        {Array.from({ length: INVOICE_COLUMN_COUNT }).map(
                          (__, colIndex) => (
                            <TableCell key={colIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                  : invoices.map((invoice: InvoiceListItem) => (
                      <InvoiceTableRow
                        key={invoice.id}
                        invoice={invoice}
                        onClick={handleInvoiceClick}
                      />
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePaginationFooter
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            onPageChange: handlePageChange,
          }}
          totalCount={totalCount}
          entityOptions={{ singular: 'invoice', includeCount: false }}
          downloadURL={downloadURL}
          downloadLabel="Export invoice data"
        />
      </Stack>
    </PageContent>
  );
}

// Empty-state copy tailored to the active status filter so the message reads
// like a real description of what the filter is hiding.
function statusFilterLabel(status: InvoiceStatusFilter): string {
  if (status === 'all') {
    return 'No invoices found';
  }
  return `No ${INVOICE_STATUS_LABELS[status as InvoiceStatus].toLowerCase()} invoices found`;
}
